import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FULL_OWNER_PERMISSIONS,
  type IApp,
  normalizeInvitePermissions,
  type NormalizedInvitePermissions,
  type UserIHaveAcceptedInviteFrom,
} from '@todesktop/shared';
import { type IGlobalState, useStore } from '../../store';
import { callFirebaseFunction } from '../firebase';
import { getAccountDisplayName } from '../util/accountDisplayName';
import { useFeatureFlag } from './useFeatureFlag';

export interface SettingsTeam {
  accountId: string;
  isOwnTeam: boolean;
  teamName: string;
  /** The name the team saved, without falling back to its owner's. */
  savedName?: string;
  logoUrl?: string;
  permissions: NormalizedInvitePermissions;
  /** The viewer's record of a team they joined; absent for their own. */
  membership?: UserIHaveAcceptedInviteFrom;
  /** Keeps Team settings links on this team, e.g. `?account=…`. */
  search: string;
}

/**
 * The team Team settings is showing: the viewer's own, or one they joined,
 * named by `?account=`. Null when they aren't in the requested team.
 */
export const useSettingsTeam = (): SettingsTeam | null => {
  const { search } = useLocation();
  const { user, acceptedUsers } = useStore((state) => ({
    user: state.user,
    acceptedUsers: state.acceptedUsers,
  }));
  if (!user) return null;

  const requested = new URLSearchParams(search).get('account');
  if (!requested || requested === user.id) {
    return {
      accountId: user.id,
      isOwnTeam: true,
      teamName: getAccountDisplayName(user),
      savedName: user.accountDisplayName,
      logoUrl: user.accountLogoUrl,
      permissions: FULL_OWNER_PERMISSIONS,
      search: '',
    };
  }

  const membership = acceptedUsers.find((team) => team.id === requested);
  if (!membership) return null;
  return {
    accountId: requested,
    isOwnTeam: false,
    teamName: getAccountDisplayName(membership),
    savedName: membership.accountDisplayName,
    logoUrl: membership.accountLogoUrl,
    permissions: normalizeInvitePermissions(membership.permissions),
    membership,
    search: `?${new URLSearchParams({ account: requested }).toString()}`,
  };
};

type AppShape = Pick<IApp, 'appType' | 'id'>;

/** Whether an account's loaded apps include a v2 app. */
const accountHasV2App = (
  accountId: string,
  { appToUser, apps }: Pick<IGlobalState, 'appToUser'> & { apps: AppShape[] },
) =>
  apps.some(
    (app) => app.appType === 'v2' && appToUser[app.id]?.id === accountId,
  );

/**
 * Whether the viewer manages signing for a team they joined that uses v2,
 * so sees its Signing page without a `v2Apps` flag of their own. Admins hold
 * "manage signing" implicitly, so the permission alone would show every v1
 * team's Admins v2's Signing page. The team's flag is on its owner's user
 * document, which members can't read, so a team counts as using v2 once it
 * has a v2 app. Null while the team's apps are still loading; once the first
 * load has settled (`appsLoaded`), a team whose apps failed to load counts
 * as not using v2.
 */
export const managesTeamV2Signing = (
  team: SettingsTeam | null,
  state: Pick<
    IGlobalState,
    'accountAppsLoaded' | 'appsLoaded' | 'appToUser'
  > & { apps: AppShape[] },
): boolean | null => {
  if (!team || team.isOwnTeam || !team.permissions.canManageSigning) {
    return false;
  }
  if (accountHasV2App(team.accountId, state)) return true;
  return state.accountAppsLoaded[team.accountId] || state.appsLoaded
    ? false
    : null;
};

/** `managesTeamV2Signing` for the store's apps. */
export const useManagesTeamV2Signing = (
  team: SettingsTeam | null,
): boolean | null => useStore((state) => managesTeamV2Signing(team, state));

/**
 * Whether an account the viewer manages uses v2, so its members' "manage
 * signing" means something: their own account by their `v2Apps` flag, a team
 * they joined once it has a v2 app (see managesTeamV2Signing).
 */
export const useAccountUsesV2 = (
  accountId: string | null,
  isOwnAccount: boolean,
): boolean => {
  const v2Apps = useFeatureFlag('v2Apps');
  const teamHasV2App = useStore((state) =>
    accountId && !isOwnAccount ? accountHasV2App(accountId, state) : false,
  );
  return isOwnAccount ? v2Apps : teamHasV2App;
};

const getAccountSso = callFirebaseFunction('getAccountSso');

// One request per team a session: the flag rarely changes, and the Members
// page, invite form and Team settings all ask.
const accountSsoRequests = new Map<string, Promise<boolean>>();

const loadAccountUsesSso = (accountId: string): Promise<boolean> => {
  let request = accountSsoRequests.get(accountId);
  if (!request) {
    request = getAccountSso({ accountId }).then(
      ({ data }) => data.enabled,
      () => {
        // Try again next time rather than remembering a failure.
        accountSsoRequests.delete(accountId);
        return false;
      },
    );
    accountSsoRequests.set(accountId, request);
  }
  return request;
};

/**
 * Whether an account the viewer manages has single sign-on (the `sso` flag),
 * so its members' "manage SSO" means something: their own account by their
 * flag, a team they joined by asking the server, since the team's flag is on
 * its owner's user document, which members can't read. False until known.
 */
export const useAccountUsesSso = (
  accountId: string | null,
  isOwnAccount: boolean,
): boolean => {
  const ownFlag = useFeatureFlag('sso');
  const [team, setTeam] = useState<{ accountId: string; enabled: boolean }>();

  useEffect(() => {
    if (!accountId || isOwnAccount) return undefined;
    let cancelled = false;
    void loadAccountUsesSso(accountId).then((enabled) => {
      if (!cancelled) setTeam({ accountId, enabled });
    });
    return () => {
      cancelled = true;
    };
  }, [accountId, isOwnAccount]);

  if (isOwnAccount) return ownFlag;
  return team?.accountId === accountId && team.enabled;
};
